import { memo, useCallback, useMemo } from 'react';
import { Group, Toolbar, ToolbarProps } from 'react-aria-components';

import { merge } from '@/components';
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ChevronIcon, DownloadIcon, RotateIcon, ZoomInIcon, ZoomOutIcon } from '@/components/icons';
import { useAgentRunTree, useGetAgentRunFromPath } from '@/hooks';
import { useDocumentStore } from '@/store';

export type DocumentToolbarProps = ToolbarProps;

export const DocumentToolbar = memo(({ className, ...rest }: DocumentToolbarProps) => {
  const zoom = useDocumentStore((state) => state.zoom);
  const setZoom = useDocumentStore((state) => state.setZoom);
  const setRotation = useDocumentStore((state) => state.setRotation);
  const documentBlob = useDocumentStore((state) => state.documentBlob);

  const { data: run } = useGetAgentRunFromPath();
  const { taskDocument } = useAgentRunTree({ agentId: run?.agentId, runId: run?.runId });

  const onDownload = useCallback(() => {
    if (documentBlob) {
      const url = URL.createObjectURL(documentBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = taskDocument?.name ?? 'download';
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, [documentBlob, taskDocument?.name]);

  const zooms = useMemo(() => {
    return [
      { id: '0.25', label: '25%' },
      { id: '0.5', label: '50%' },
      { id: '0.7', label: '70%' },
      { id: '0.85', label: '85%' },
      { id: '1', label: '100%' },
      { id: '1.25', label: '125%' },
      { id: '1.5', label: '150%' },
      { id: '1.75', label: '175%' },
      { id: '2', label: '200%' },
      { id: '3', label: '300%' },
      { id: '4', label: '400%' },
    ];
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((previousZoom) => {
      const previousIndex = zooms.findIndex((z) => z.id === previousZoom.toString());
      let newZoom = previousZoom;
      if (previousIndex !== -1) {
        const newIndex = previousIndex - 1;
        if (newIndex >= 0) newZoom = Number(zooms[newIndex].id);
      }
      return newZoom;
    });
  }, [setZoom, zooms]);

  const zoomIn = useCallback(() => {
    setZoom((previousZoom) => {
      const previousIndex = zooms.findIndex((z) => z.id === previousZoom.toString());
      let newZoom = previousZoom;
      if (previousIndex !== -1) {
        const newIndex = previousIndex + 1;
        if (newIndex < zooms.length) newZoom = Number(zooms[newIndex].id);
      }
      return newZoom;
    });
  }, [setZoom, zooms]);

  return (
    <Toolbar orientation="horizontal" className={merge('gap-sm flex items-center', className)} {...rest}>
      <Group className="gap-sm flex flex-row items-center" aria-label="Rotation">
        <TooltipTrigger>
          <Button onPress={() => setRotation((previousRotation) => (previousRotation - 90) % 360)} variant="plain">
            <RotateIcon rotation="ccw" />
          </Button>
          <Tooltip variant="primary">
            <Text>Rotate left</Text>
          </Tooltip>
        </TooltipTrigger>

        <TooltipTrigger>
          <Button onPress={() => setRotation((previousRotation) => (previousRotation + 90) % 360)} variant="plain">
            <RotateIcon rotation="cw" />
          </Button>
          <Tooltip variant="primary">
            <Text>Rotate right</Text>
          </Tooltip>
        </TooltipTrigger>
      </Group>
      <Group className="gap-sm flex flex-row items-center" aria-label="Zoom">
        <TooltipTrigger>
          <Button onPress={zoomOut} variant="plain">
            <ZoomOutIcon />
          </Button>
          <Tooltip variant="primary">
            <Text>Zoom out</Text>
          </Tooltip>
        </TooltipTrigger>

        <TooltipTrigger>
          <Button onPress={zoomIn} variant="plain">
            <ZoomInIcon />
          </Button>
          <Tooltip variant="primary">
            <Text>Zoom in</Text>
          </Tooltip>
        </TooltipTrigger>

        <Select
          selectedKey={zoom.toString()}
          onSelectionChange={(zoom) => setZoom(Number(zoom))}
          className="flex items-start"
        >
          <TooltipTrigger>
            <Button variant="secondary" className="w-fit py-0.5 pr-1 pl-1.5">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
            <Tooltip variant="primary">
              <Text slot="description">Zoom level</Text>
            </Tooltip>
          </TooltipTrigger>
          <Popover>
            <ListBox>
              {zooms.map((z) => (
                <ListBoxItem key={z.id} id={z.id}>
                  <Text>{z.label}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>
      </Group>
      <Group className="gap-sm flex flex-row items-center" aria-label="Download">
        <TooltipTrigger>
          <Button variant="plain" onPress={onDownload}>
            <DownloadIcon />
          </Button>
          <Tooltip variant="primary">
            <Text>Download original document</Text>
          </Tooltip>
        </TooltipTrigger>
      </Group>
    </Toolbar>
  );
});
